import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import RadioPlayer from "../../Dashboard/RadioPlayer";

export default function MobileNav() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { user, profile, signOut } = useAuth();

  const isActivePath = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };
  // Can be null, 'services', a number, or an array containing 'services' and category indices
  const [activeCategory, setActiveCategory] = useState(null);

  // Handle body scroll locking
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
      // Reset active category when menu closes
      setActiveCategory(null);
    }
    
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const toggleCategory = (index) => {
    // If clicking on 'services', toggle services menu
    if (index === 'services') {
      // If services is being opened, but a category is already open,
      // keep that category open
      setActiveCategory(activeCategory === 'services' ? null : 
        (activeCategory !== null && typeof activeCategory === 'number' ? 
          [activeCategory, 'services'] : 'services'));
    } 
    // If clicking on a category number
    else if (typeof index === 'number') {
      // If services isn't open yet, open both services and this category
      if (activeCategory !== 'services' && !Array.isArray(activeCategory)) {
        setActiveCategory(['services', index]);
      }
      // If services is open, toggle this category
      else {
        // If this category is already open, close it but keep services open
        if (Array.isArray(activeCategory) && activeCategory.includes(index)) {
          const newActive = activeCategory.filter(item => item !== index);
          setActiveCategory(newActive.length === 1 ? newActive[0] : newActive);
        } 
        // Otherwise open this category while keeping services open
        else {
          setActiveCategory(Array.isArray(activeCategory) ? 
            [...activeCategory.filter(item => item !== index), index] : 
            ['services', index]);
        }
      }
    }
  };

  const handleLinkClick = () => {
    setIsOpen(false);
    setActiveCategory(null);
  };
  
  // Helper function to check if we need to show the arrow as active
  const isActive = (value) => {
    if (activeCategory === value) return true;
    if (Array.isArray(activeCategory) && activeCategory.includes(value)) return true;
    return false;
  };

  const menuCategories = [
    {
      title: "Web Solutions",
      icon: "/images/services/menu1.svg",
      items: [
        { name: "Website Design & Development", path: "/services/web/website-design-development" },
        { name: "Ecommerce Development", path: "/services/web/ecommerce-development" },
        { name: "Website Maintenance", path: "/services/web/website-maintenance" },
        { name: "Copy Writing", path: "/services/web/copywriting" },
      ],
    },
    {
      title: "Software Solutions",
      icon: "/images/services/menu2.svg",
      items: [
        { name: "Web Applications", path: "/services/software/web-applications" },
        { name: "Mobile Applications", path: "/services/software/mobile-applications" },
        { name: "API & System Integration", path: "/services/software/api-integration" },
        { name: "SaaS Solutions", path: "/services/software/saas-solutions" },
        { name: "MVP Solutions", path: "/services/software/mvp-solutions" },
        { name: "Maintenance Supply", path: "/services/software/maintenance-supply" },
      ],
    },
    {
      title: "AI Solutions",
      icon: "/images/services/menu3.svg",
      items: [
        { name: "AI Chatbots", path: "/services/artificial-intelligence/ai-chatbots" },
        { name: "SaaS AI", path: "/services/artificial-intelligence/saas-ai" },
        { name: "AI PoC & MVP", path: "/services/artificial-intelligence/ai-poc-ai-mvp" },
      ],
    },
    {
      title: "Digital Marketing",
      icon: "/images/services/menu4.svg",
      items: [
        { name: "Search Engine Optimization", path: "/services/digital-marketing/seo" },
        { name: "Social Media Marketing", path: "/services/digital-marketing/smm" },
        { name: "Google Ads or PPC Marketing", path: "/services/digital-marketing/ppc" },
        { name: "Online Reputation Management", path: "/services/digital-marketing/reputation" },
      ],
    },
    {
      title: "Branding",
      icon: "/images/services/menu5.svg",
      items: [
        { name: "Brand Identity", path: "/services/branding/identity" },
        { name: "Logo Design", path: "/services/branding/logo-design" },
        { name: "Printing Materials", path: "/services/branding/printing" },
        { name: "Brand Launch", path: "/services/branding/launch" },
      ],
    },
    {
      title: "Hosting Services",
      icon: "/images/services/menu6.svg",
      items: [
        { name: "Domain Registration", path: "/services/hosting/domain-registration" },
        { name: "Website Hosting", path: "/services/hosting/website" },
        { name: "Email Hosting", path: "/services/hosting/email" },
        { name: "Security & SSL", path: "/services/hosting/security" },
      ],
    },
  ];

  return (
    <>
      {/* Mobile header with logo, radio, and hamburger */}
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-black z-50 px-3 py-2 flex justify-between items-center border-b border-gray-800 shadow-lg gap-2">
        <Link to="/" className="z-50 flex-shrink-0">
          <img src="/images/logo.svg" alt="Logo" className="h-9" />
        </Link>

        {/* Radio player — compact, centered */}
        <div className="flex-1 min-w-0 flex justify-center">
          <RadioPlayer />
        </div>

        <button
          onClick={toggleMenu}
          className="z-50 flex-shrink-0 text-white focus:outline-none w-9 h-9 flex items-center justify-center rounded-md hover:bg-gray-900 transition-colors"
          aria-label="Toggle menu"
        >
          {isOpen ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          )}
        </button>
      </div>

      {/* Mobile menu overlay with improved animation */}
      <div
        className={`fixed inset-0 bg-black/95 z-40 transform transition-all duration-300 ease-in-out lg:hidden ${
          isOpen ? "translate-x-0 opacity-100" : "-translate-x-full opacity-0"
        }`}
      >
        <div className="h-full overflow-y-auto pt-2 pb-24 overscroll-contain">
          {/* Main navigation links */}
          <nav className="px-4 py-2">
            <ul className="space-y-1">
              <li>
                <Link
                  to="/about-us"
                  className={`flex items-center py-3 px-4 text-lg font-medium rounded-lg hover:bg-gray-800 transition-colors ${isActivePath('/about-us') ? 'text-yellow-500' : 'text-white'}`}
                  onClick={handleLinkClick}
                >
                  About Us
                </Link>
              </li>

              {/* Services navigation with accordion */}
              <li className="py-1">
                <div className="rounded-lg overflow-hidden">
                  <button
                    onClick={() => toggleCategory('services')}
                    className="flex items-center justify-between w-full py-3 px-4 text-white text-lg font-medium hover:bg-gray-800 rounded-lg transition-colors"
                    aria-expanded={activeCategory === 'services' || (Array.isArray(activeCategory) && activeCategory.includes('services'))}
                  >
                    <span>Services</span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className={`h-5 w-5 transition-transform duration-200 ${
                        activeCategory === 'services' ? "rotate-180" : "rotate-0"
                      }`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  {/* Services categories */}
                  <div 
                    className={`overflow-hidden transition-all duration-300 ease-in-out ${
                      activeCategory === 'services' || (Array.isArray(activeCategory) && activeCategory.includes('services')) 
                        ? 'max-h-screen opacity-100' 
                        : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="px-2 py-3 bg-gray-900/50 rounded-lg mt-1">
                      {/* Service category accordions */}
                      {menuCategories.map((category, index) => (
                        <div key={index} className="mb-2 last:mb-0 overflow-hidden rounded-lg">
                          <button
                            onClick={() => toggleCategory(index)}
                            className="flex items-center justify-between w-full p-3 text-left text-white rounded-lg bg-gray-800/50 hover:bg-gray-800 transition-colors"
                          >
                            <div className="flex items-center">
                              <img src={category.icon} alt="" className="w-5 h-5 mr-3" />
                              <span className="font-medium">{category.title}</span>
                            </div>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className={`h-4 w-4 transition-transform duration-200 ${
                                (activeCategory === index || (Array.isArray(activeCategory) && activeCategory.includes(index))) 
                                  ? "rotate-180 text-yellow-500" 
                                  : "rotate-0 text-gray-400"
                              }`}
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                          </button>

                          <div 
                            className={`overflow-hidden transition-all duration-200 ease-in-out ${
                              (activeCategory === index || (Array.isArray(activeCategory) && activeCategory.includes(index))) 
                                ? 'max-h-screen opacity-100' 
                                : 'max-h-0 opacity-0'
                            }`}
                          >
                            <ul className="p-3 bg-gray-900/30 rounded-b-lg space-y-1">
                              {category.items.map((item, idx) => (
                                <li key={idx}>
                                  <Link
                                    to={item.path}
                                    className="block px-3 py-2 text-gray-300 hover:text-yellow-500 hover:bg-black/30 rounded transition-colors text-sm"
                                    onClick={handleLinkClick}
                                  >
                                    {item.name}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </li>

              <li>
                <Link
                  to="/portfolio"
                  className={`flex items-center py-3 px-4 text-lg font-medium rounded-lg hover:bg-gray-800 transition-colors ${isActivePath('/portfolio') ? 'text-yellow-500' : 'text-white'}`}
                  onClick={handleLinkClick}
                >
                  Portfolio
                </Link>
              </li>

              <li>
                <Link
                  to="/contact-us"
                  className={`flex items-center py-3 px-4 text-lg font-medium rounded-lg hover:bg-gray-800 transition-colors ${isActivePath('/contact-us') ? 'text-yellow-500' : 'text-white'}`}
                  onClick={handleLinkClick}
                >
                  Contact Us
                </Link>
              </li>
            </ul>
          </nav>

          {/* Contact info in mobile menu */}
          <div className="px-6 mt-8">
            <h3 className="text-gray-400 uppercase text-sm font-medium mb-4">Get in touch</h3>

            <div className="space-y-4">
              <div className="flex items-start">
                <div className="flex justify-center items-center w-10 h-10 rounded-full bg-yellow-500/10 mr-3">
                  <img className="w-4 h-4" src="/images/call.svg" alt="Phone" />
                </div>
                <div>
                  <Link
                    to="tel:+18259063078"
                    className="text-gray-300 hover:text-yellow-500 transition-colors"
                  >
                    +1 825 906 3078
                  </Link>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex justify-center items-center w-10 h-10 rounded-full bg-yellow-500/10 mr-3">
                  <img className="w-4 h-4" src="/images/mail.svg" alt="Email" />
                </div>
                <div>
                  <Link
                    to="mailto:Outreach@quickgrowth.ai"
                    className="text-gray-300 hover:text-yellow-500 transition-colors"
                  >
                    Outreach@quickgrowth.ai
                  </Link>
                </div>
              </div>
            </div>

            <div className="mt-8 px-4 space-y-3">
              {user ? (
                <>
                  <div className="flex items-center space-x-3 px-2 py-3 rounded-lg bg-white/5 border border-yellow-500/20">
                    <div className="w-10 h-10 rounded-full bg-yellow-500/20 border border-yellow-500/40 flex items-center justify-center flex-shrink-0">
                      <span className="text-yellow-500 font-bold">
                        {(profile?.full_name || user.email || '?')[0].toUpperCase()}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-white text-sm font-medium truncate">{profile?.full_name || 'User'}</p>
                      <p className="text-gray-400 text-xs truncate">{user.email}</p>
                    </div>
                  </div>
                  <Link
                    to="/dashboard"
                    className="block text-center w-full px-4 py-2 bg-[#C9A84C] hover:bg-[#A8863C] text-black text-sm font-semibold rounded tracking-wide transition-colors duration-200"
                    onClick={handleLinkClick}
                  >
                    Dashboard
                  </Link>
                  <button
                    onClick={() => { signOut(); handleLinkClick(); }}
                    className="block w-full px-4 py-2 border border-[#C9A84C]/30 text-[#C9A84C] hover:bg-[#C9A84C]/10 hover:border-[#C9A84C]/60 text-sm font-medium rounded tracking-wide transition-colors duration-200"
                  >
                    Log Out
                  </button>
                </>
              ) : (
                <div className="flex items-center justify-center gap-4 py-2">
                  <Link
                    to="/login"
                    className="text-sm text-white/80 hover:text-yellow-400 font-medium transition-colors duration-200"
                    onClick={handleLinkClick}
                  >
                    Login
                  </Link>
                  <span className="w-px h-4 bg-white/25 flex-shrink-0" />
                  <Link
                    to="/signup"
                    className="text-sm text-white/80 hover:text-yellow-400 font-medium transition-colors duration-200"
                    onClick={handleLinkClick}
                  >
                    Create Account
                  </Link>
                </div>
              )}
            </div>
            <div className="flex justify-center gap-4 pt-2 pb-1">
              <Link to="/terms" onClick={handleLinkClick} className="text-gray-500 hover:text-gray-300 text-xs transition-colors">Terms of Service</Link>
              <span className="text-gray-700 text-xs">·</span>
              <Link to="/privacy" onClick={handleLinkClick} className="text-gray-500 hover:text-gray-300 text-xs transition-colors">Privacy Policy</Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}